/**
 * 인증 API 서비스
 *
 * AICC 관리자/상담원 로그인·로그아웃 처리.
 * httpOnly 쿠키 기반 인증으로 XSS 공격을 방지하며,
 * 브라우저가 credentials: 'include'로 쿠키를 자동 전송.
 */

const API_BASE = '/api/auth';

/**
 * Login with email and password.
 * Server sets httpOnly cookies (access_token, refresh_token) in the response.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{user: object}>}
 */
export async function login(email, password) {
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.message || '로그인에 실패했습니다.', response.status, error.code);
  }

  const data = await response.json();

  // Clear any legacy localStorage tokens from previous versions
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  return data;
}

/**
 * Logout current user.
 * Server clears httpOnly cookies.
 *
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
  } finally {
    // Clear any legacy localStorage tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
}

/**
 * Refresh access token.
 * The refresh_token httpOnly cookie is sent automatically.
 *
 * @returns {Promise<object>}
 */
export async function refreshAccessToken() {
  const response = await fetch(`${API_BASE}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: '{}',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.message || '토큰 갱신에 실패했습니다.', response.status, error.code);
  }

  return response.json();
}

/**
 * Get current user profile.
 * The access_token httpOnly cookie is sent automatically.
 * On 401, attempts one token refresh before giving up.
 *
 * @returns {Promise<object>}
 */
export async function getCurrentUser() {
  const response = await fetch(`${API_BASE}/me`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      try {
        await refreshAccessToken();
        return getCurrentUser();
      } catch {
        throw new AuthError('세션이 만료되었습니다.', 401, 'SESSION_EXPIRED');
      }
    }

    const error = await response.json().catch(() => ({}));
    throw new AuthError(
      error.message || '사용자 정보를 가져오는데 실패했습니다.',
      response.status,
      error.code
    );
  }

  return response.json();
}

/**
 * Change user password.
 *
 * @param {string} currentPassword
 * @param {string} newPassword
 * @param {string} confirmPassword
 * @returns {Promise<void>}
 */
export async function changePassword(currentPassword, newPassword, confirmPassword) {
  const response = await fetch(`${API_BASE}/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(
      error.message || '비밀번호 변경에 실패했습니다.',
      response.status,
      error.code
    );
  }
}

/**
 * Custom error class for authentication errors.
 */
export class AuthError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
    this.code = code;
  }
}

// Removed functions (no longer needed with httpOnly cookies):
// - getAccessToken() — cookies are sent automatically
// - getRefreshToken() — cookies are sent automatically
// - setTokens() — server sets cookies
// - clearTokens() — server clears cookies on logout
// - isAuthenticated() — use AuthContext.isAuthenticated instead

const authApi = {
  login,
  logout,
  refreshAccessToken,
  getCurrentUser,
  changePassword,
  AuthError,
};

export default authApi;
